import { Star, MessageCircle, Phone, MapPin } from "lucide-react";
import { Button } from "../ui/button";
import { CLINIC, telLink, waLink, PHOTOS } from "../../lib/clinic";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden bg-grain"
    >
      {/* Decorative EKG line behind */}
      <svg
        className="absolute inset-x-0 top-1/2 -translate-y-1/2 w-full h-40 opacity-[0.07] pointer-events-none"
        viewBox="0 0 1200 200"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          className="ekg-line"
          d="M0,100 L200,100 L240,100 L260,40 L290,160 L320,100 L600,100 L640,100 L660,40 L690,160 L720,100 L1200,100"
          fill="none"
          stroke="hsl(var(--soft-red))"
          strokeWidth="3"
        />
      </svg>

      <div className="max-w-7xl mx-auto px-5 md:px-8 relative z-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* LEFT: copy */}
          <div className="lg:col-span-7 reveal">
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] text-[hsl(var(--navy))] tracking-tight">
              Tu corazón en manos
              <br />
              de un{" "}
              <span className="italic font-light">
                experto<span className="text-[hsl(var(--soft-red))]">.</span>
              </span>
            </h1>

            <p className="mt-6 max-w-xl text-base md:text-lg text-[hsl(var(--navy-soft))] leading-relaxed">
              El <strong className="font-medium text-[hsl(var(--navy))]">{CLINIC.doctorName}</strong> combina
              {" "}{CLINIC.yearsExperience} años de experiencia en medicina crítica y cardiología para ofrecerte
              diagnósticos precisos y un trato humano que marca la diferencia.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href={waLink()} target="_blank" rel="noreferrer" data-testid="hero-cta-whatsapp">
                <Button
                  size="lg"
                  className="rounded-full h-12 px-7 bg-[hsl(var(--soft-red))] hover:bg-[hsl(var(--soft-red))]/90 text-white shadow-warm w-full sm:w-auto text-[15px]"
                >
                  <MessageCircle className="w-5 h-5" />
                  Agendar por WhatsApp
                </Button>
              </a>
              <a href={telLink} data-testid="hero-cta-call">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full h-12 px-7 border-[hsl(var(--navy))] text-[hsl(var(--navy))] hover:bg-[hsl(var(--navy))] hover:text-[hsl(var(--cream))] w-full sm:w-auto text-[15px]"
                >
                  <Phone className="w-5 h-5" /> Línea directa
                </Button>
              </a>
            </div>

            {/* Social proof */}
            <div className="mt-10 flex items-center gap-4 flex-wrap" data-testid="hero-social-proof">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[hsl(var(--gold))] text-[hsl(var(--gold))]" />
                ))}
              </div>
              <p className="text-sm text-[hsl(var(--navy-soft))]">
                <span className="font-medium text-[hsl(var(--navy))]">"Excelente atención y explicación detallada"</span>
                <span className="mx-2">·</span>
                <span>+{CLINIC.rating.reviews} pacientes satisfechos</span>
              </p>
            </div>

            {/* Trust strip */}
            <div className="mt-8 pt-6 border-t border-[hsl(var(--border))] grid grid-cols-3 gap-4 max-w-md">
              <Stat value="15+" label="Años de experiencia" />
              <Stat value="ABC" label="Torre Annie Cass" />
              <Stat value="UNAM" label="Alta especialidad" />
            </div>
          </div>

          {/* RIGHT: portrait */}
          <div className="lg:col-span-5 reveal" style={{ animationDelay: "0.15s" }}>
            <div className="relative" data-testid="hero-portrait">
              {/* Soft red blob */}
              <div className="absolute -top-6 -right-6 w-40 h-40 rounded-full bg-[hsl(var(--soft-red))]/15 blur-2xl" />
              <div className="absolute -bottom-8 -left-6 w-48 h-48 rounded-full bg-[hsl(var(--navy))]/10 blur-2xl" />

              <div className="relative rounded-[28px] overflow-hidden border border-[hsl(var(--border))] shadow-soft bg-white">
                <img
                  src={PHOTOS.hero}
                  alt="Dr. Luis Rodrigo González Azuara, Cardiólogo"
                  className="w-full h-[480px] md:h-[560px] object-cover"
                  loading="eager"
                />
                <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-[hsl(var(--navy))]/85 to-transparent text-white">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] opacity-90">
                    <MapPin className="w-3.5 h-3.5" /> Hospital MAC Norte · Consultorio 527
                  </div>
                  <div className="mt-1 font-serif-display text-xl">Cardiología de alta especialidad</div>
                </div>
              </div>

              {/* Floating credentials card */}
              <div className="hidden md:block absolute -left-8 bottom-12 bg-white rounded-2xl border border-[hsl(var(--border))] shadow-soft p-4 max-w-[220px]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--navy-soft))]">Disponible hoy</span>
                </div>
                <p className="text-sm text-[hsl(var(--navy))] leading-snug">
                  Diagnóstico el <span className="font-medium">mismo día</span> de tu consulta.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Stat = ({ value, label }) => (
  <div>
    <div className="font-serif-display text-2xl text-[hsl(var(--navy))]">{value}</div>
    <div className="text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--navy-soft))] mt-1">{label}</div>
  </div>
);
